import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SearchMatch {
  id: string;
  score: number;
  reason: string;
}

interface SemanticSearchState {
  matches: SearchMatch[];
  isSearching: boolean;
  error: string | null;
}

interface SearchableItem {
  id: string;
  name: string | null;
  ai_summary?: string | null;
  ai_category?: string | null;
  ai_tags?: string[] | null;
  store?: string | null;
}

export function useSemanticSearch<T extends SearchableItem>(items: readonly T[]) {
  const [state, setState] = useState<SemanticSearchState>({
    matches: [],
    isSearching: false,
    error: null,
  });
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const cacheRef = useRef<Map<string, SearchMatch[]>>(new Map());

  const search = useCallback(async (query: string) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const trimmedQuery = query.trim().toLowerCase();
    
    if (!trimmedQuery || trimmedQuery.length < 2) {
      setState({ matches: [], isSearching: false, error: null });
      return;
    }

    // Check cache first
    const cacheKey = `${trimmedQuery}-${items.length}`;
    if (cacheRef.current.has(cacheKey)) {
      setState({ matches: cacheRef.current.get(cacheKey)!, isSearching: false, error: null });
      return;
    }

    setState(prev => ({ ...prev, isSearching: true, error: null }));
    abortControllerRef.current = new AbortController();

    try {
      // Prepare minimal metadata for the API
      const itemMetadata = items.map(item => ({
        id: item.id,
        name: item.name,
        ai_summary: item.ai_summary,
        ai_category: item.ai_category,
        ai_tags: item.ai_tags,
        store: item.store,
      }));

      const { data, error } = await supabase.functions.invoke('semantic-search-saves', {
        body: { query: trimmedQuery, items: itemMetadata },
      });

      if (error) throw error;

      const matches = data?.matches || [];
      
      // Cache the result
      cacheRef.current.set(cacheKey, matches);
      
      // Limit cache size
      if (cacheRef.current.size > 20) {
        const firstKey = cacheRef.current.keys().next().value;
        if (firstKey) cacheRef.current.delete(firstKey);
      }

      setState({ matches, isSearching: false, error: null });
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') return;
      
      console.error('Semantic search failed:', err);
      setState({ 
        matches: [], 
        isSearching: false, 
        error: err instanceof Error ? err.message : 'Search failed' 
      });
    }
  }, [items]);

  const clearSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setState({ matches: [], isSearching: false, error: null });
  }, []);

  return {
    ...state,
    search,
    clearSearch,
  };
}
